import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react'
import { Box, HStack, IconButton, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Text, Tooltip } from '@chakra-ui/react'
import { Play, Pause, ArrowCounterClockwise, SkipBack, SkipForward } from '@phosphor-icons/react'
import RubiksCube from './RubiksCube'

interface CubeAnimatorProps {
  algorithm?: string
  setupMoves?: string        // Moves to apply before algorithm (e.g., inverse for OLL setup)
  showCase?: boolean         // If true, automatically apply inverse to show the case first
  autoPlay?: boolean
  speed?: number             // ms per move (default 500)
  showControls?: boolean     // Show playback controls
  interactive?: boolean      // Allow orbit controls
  onComplete?: () => void
  onMoveChange?: (moveIndex: number, move: string) => void
  size?: string | number     // Container size
  bgColor?: string           // Background color (default: transparent)
  visiblePieces?: string[]   // Pieces to show fully (e.g., ['U', 'UF', 'UFR'])
  hiddenOpacity?: number     // Opacity for hidden pieces (default 0.15)
}

export interface CubeAnimatorRef {
  play: () => void
  pause: () => void
  reset: () => void
  nextMove: () => void
  prevMove: () => void
  goToMove: (index: number) => void
}

// Parse an algorithm string into individual moves
const parseAlgorithm = (algorithm: string): string[] => {
  if (!algorithm || algorithm.trim() === '') return []

  // Handle common algorithm notations
  const normalized = algorithm
    .replace(/\(/g, ' ')
    .replace(/\)/g, ' ')
    .replace(/\[/g, ' ')
    .replace(/\]/g, ' ')
    .trim()

  // Split by whitespace and filter empty strings
  const moves = normalized.split(/\s+/).filter(move => move.length > 0)

  return moves
}

// Get the inverse of a move
const getInverseMove = (move: string): string => {
  if (move.endsWith("'")) {
    return move.slice(0, -1)
  } else if (move.endsWith('2')) {
    return move // Double moves are their own inverse
  } else {
    return move + "'"
  }
}

// Get the inverse of an algorithm
const getInverseAlgorithm = (algorithm: string): string[] => {
  const moves = parseAlgorithm(algorithm)
  return moves.reverse().map(getInverseMove)
}

const CubeAnimator = forwardRef<CubeAnimatorRef, CubeAnimatorProps>(({
  algorithm = '',
  setupMoves = '',
  showCase = false,
  autoPlay = false,
  speed = 500,
  showControls = true,
  interactive = true,
  onComplete,
  onMoveChange,
  size = '300px',
  bgColor = 'transparent',
  visiblePieces,
  hiddenOpacity = 0.15
}, ref) => {
  const [moves, setMoves] = useState<string[]>([])
  const [currentMoveIndex, setCurrentMoveIndex] = useState(-1)
  const [currentMove, setCurrentMove] = useState<string | undefined>(undefined)
  const [isPlaying, setIsPlaying] = useState(false)
  const [initialMoves, setInitialMoves] = useState<string[]>([])  // For instant setup
  const cubeRef = useRef<any>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const cubeKey = useRef(0) // Key to force re-mount cube

  // Parse algorithm and setup moves on change
  useEffect(() => {
    const parsedMoves = parseAlgorithm(algorithm)
    setMoves(parsedMoves)

    // Determine setup moves (applied instantly, no animation):
    // 1. First apply any setupMoves (e.g., x2 to rotate cube orientation)
    // 2. Then if showCase is true, also apply the inverse of the algorithm
    let parsedSetup: string[] = []
    if (setupMoves) {
      parsedSetup = parseAlgorithm(setupMoves)
    }
    if (showCase && algorithm) {
      parsedSetup = [...parsedSetup, ...getInverseAlgorithm(algorithm)]
    }
    setInitialMoves(parsedSetup)

    // Reset state
    setCurrentMoveIndex(-1)
    setCurrentMove(undefined)
    setIsPlaying(false)

    // Force re-mount cube to reset
    cubeKey.current += 1

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [algorithm, setupMoves, showCase])

  // Handle auto-play
  useEffect(() => {
    if (autoPlay && moves.length > 0) {
      // Small delay before starting
      const timer = setTimeout(() => {
        setIsPlaying(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [autoPlay, moves])

  // Handle move completion
  const handleMoveComplete = useCallback(() => {
    const nextIndex = currentMoveIndex + 1
    if (nextIndex < moves.length && isPlaying) {
      timeoutRef.current = setTimeout(() => {
        setCurrentMoveIndex(nextIndex)
        setCurrentMove(moves[nextIndex])
        onMoveChange?.(nextIndex, moves[nextIndex])
      }, speed)
    } else if (nextIndex >= moves.length) {
      // Algorithm complete
      setIsPlaying(false)
      setCurrentMove(undefined)
      onComplete?.()
    }
  }, [currentMoveIndex, moves, isPlaying, speed, onComplete, onMoveChange])

  // Start playing
  const play = useCallback(() => {
    if (moves.length === 0) return

    setIsPlaying(true)

    if (currentMoveIndex < 0 || currentMoveIndex >= moves.length - 1) {
      // Start from beginning
      setCurrentMoveIndex(0)
      setCurrentMove(moves[0])
      onMoveChange?.(0, moves[0])
    } else {
      // Continue from current position
      setCurrentMove(moves[currentMoveIndex])
    }
  }, [moves, currentMoveIndex, onMoveChange])

  // Pause playing
  const pause = useCallback(() => {
    setIsPlaying(false)
    setCurrentMove(undefined)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Reset to beginning
  const reset = useCallback(() => {
    setIsPlaying(false)
    setCurrentMoveIndex(-1)
    setCurrentMove(undefined)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    // Force re-mount cube
    cubeKey.current += 1
  }, [])

  // Go to next move manually
  const nextMove = useCallback(() => {
    if (currentMoveIndex < moves.length - 1) {
      const nextIndex = currentMoveIndex + 1
      setCurrentMoveIndex(nextIndex)
      setCurrentMove(moves[nextIndex])
      onMoveChange?.(nextIndex, moves[nextIndex])
    }
  }, [currentMoveIndex, moves, onMoveChange])

  // Go to previous move (by resetting and replaying up to index)
  const prevMove = useCallback(() => {
    if (currentMoveIndex > 0) {
      // We need to reset and replay up to the previous move
      // This is simplified - just reset for now
      const targetIndex = currentMoveIndex - 1
      reset()

      // After reset, replay setup and moves up to targetIndex
      setTimeout(() => {
        // Apply all setup moves instantly (no animation)
        // Then set state to target index
        setCurrentMoveIndex(targetIndex)
        onMoveChange?.(targetIndex, moves[targetIndex])
      }, 100)
    }
  }, [currentMoveIndex, moves, reset, onMoveChange])

  // Go to specific move index
  const goToMove = useCallback((index: number) => {
    if (index >= 0 && index < moves.length) {
      reset()
      setTimeout(() => {
        setCurrentMoveIndex(index)
        onMoveChange?.(index, moves[index])
      }, 100)
    }
  }, [moves, reset, onMoveChange])

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    play,
    pause,
    reset,
    nextMove,
    prevMove,
    goToMove
  }), [play, pause, reset, nextMove, prevMove, goToMove])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Box>
      <Box
        width={size}
        height={size}
        borderRadius="lg"
        overflow="hidden"
        bg={bgColor}
        position="relative"
      >
        <RubiksCube
          key={cubeKey.current}
          ref={cubeRef}
          currentMove={currentMove}
          onMoveComplete={handleMoveComplete}
          initialMoves={initialMoves}
          visiblePieces={visiblePieces}
          hiddenOpacity={hiddenOpacity}
        />

        {/* BIG move letter overlay - shown during animation */}
        {currentMoveIndex >= 0 && currentMoveIndex < moves.length && (
          <Box
            position="absolute"
            bottom="8px"
            right="8px"
            bg="blackAlpha.700"
            borderRadius="lg"
            px={4}
            py={2}
            minW="60px"
            textAlign="center"
          >
            <Text
              fontSize="3xl"
              fontWeight="bold"
              color="white"
              fontFamily="mono"
              lineHeight="1"
            >
              {moves[currentMoveIndex]}
            </Text>
          </Box>
        )}
      </Box>

      {showControls && moves.length > 0 && (
        <Box mt={3}>
          {/* Move counter */}
          <Text fontSize="sm" color="gray.500" textAlign="center" mb={2}>
            {currentMoveIndex >= 0 && currentMoveIndex < moves.length ? (
              `Move ${currentMoveIndex + 1} of ${moves.length}`
            ) : (
              'Ready'
            )}
          </Text>

          {/* Progress slider */}
          <Slider
            value={currentMoveIndex + 1}
            min={0}
            max={moves.length}
            step={1}
            onChange={(val) => goToMove(val - 1)}
            isDisabled={isPlaying}
            mb={2}
          >
            <SliderTrack bg="gray.600">
              <SliderFilledTrack bg="blue.400" />
            </SliderTrack>
            <SliderThumb boxSize={4} />
          </Slider>

          {/* Playback controls */}
          <HStack justify="center" spacing={2}>
            <Tooltip label="Reset">
              <IconButton
                aria-label="Reset"
                icon={<ArrowCounterClockwise size={18} />}
                size="sm"
                variant="ghost"
                onClick={reset}
              />
            </Tooltip>

            <Tooltip label="Previous move">
              <IconButton
                aria-label="Previous"
                icon={<SkipBack size={18} />}
                size="sm"
                variant="ghost"
                onClick={prevMove}
                isDisabled={currentMoveIndex <= 0}
              />
            </Tooltip>

            <Tooltip label={isPlaying ? 'Pause' : 'Play'}>
              <IconButton
                aria-label={isPlaying ? 'Pause' : 'Play'}
                icon={isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
                size="md"
                colorScheme="blue"
                onClick={isPlaying ? pause : play}
              />
            </Tooltip>

            <Tooltip label="Next move">
              <IconButton
                aria-label="Next"
                icon={<SkipForward size={18} />}
                size="sm"
                variant="ghost"
                onClick={nextMove}
                isDisabled={currentMoveIndex >= moves.length - 1}
              />
            </Tooltip>
          </HStack>

          {/* Algorithm display */}
          <Text
            fontSize="xs"
            color="gray.400"
            textAlign="center"
            mt={2}
            fontFamily="mono"
            wordBreak="break-all"
          >
            {moves.map((move, i) => (
              <Text
                as="span"
                key={i}
                color={i === currentMoveIndex ? 'blue.400' : i < currentMoveIndex ? 'gray.500' : 'gray.400'}
                fontWeight={i === currentMoveIndex ? 'bold' : 'normal'}
              >
                {move}{i < moves.length - 1 ? ' ' : ''}
              </Text>
            ))}
          </Text>
        </Box>
      )}
    </Box>
  )
})

CubeAnimator.displayName = 'CubeAnimator'

export default CubeAnimator
